import { openToast } from './components/toast';
import { state } from './state';
import { transport } from './firebaseTransport';
import { SoundMeter } from './helpers/soundMeter';
const MUTE_AUDIO_BY_DEFAULT = false;
var ICE_SERVERS = [
	{ urls: ['stun:stun.l.google.com:19302'] }
];

/* our own microphone / webcam */
export let local_media_stream: MediaStream = null;
export let local_media: HTMLAudioElement = null;
export let local_video_stream: MediaStream = null;
export let local_media_promise = Promise.resolve();

let localMeter: SoundMeter = null;
let force_turn = false;

/* keep track of our peer connections, indexed by peer_id (aka socket.io id) */
const peers = new Map<string, Peer>();
const polite = true;

class Peer {
	id: string;
	conn: RTCPeerConnection;
	dc: RTCDataChannel;
	makingOffer = false;
	ignoreOffer = false;
	attempts = 1;
	audio: HTMLAudioElement;
	video: MediaStream;
	meter: SoundMeter;
	signalingChain: Promise<void> = Promise.resolve();

	constructor(id: string, attempts = 0) {
		this.id = id;

		this.conn = new RTCPeerConnection({
			iceServers: ICE_SERVERS,
			iceTransportPolicy: force_turn ? 'relay' : 'all'
		});

		this.conn.onnegotiationneeded = this.onnegotiationneeded.bind(this);

		this.conn.onicecandidate = event => {
			if (event.candidate) {
				transport
					.sendSignal(id, {
						type: 'webrtc_ice_candidate',
						payload: {
							peer_id: state.user.id,
							ice_candidate: event.candidate.toJSON()
						}
					})
					.catch(err => console.error('ice candidate signal failed', err));
			}
		};

		this.conn.ontrack = event => {
			let stream = event.streams[0];
			if (event.track.kind === 'audio') {
				if (!this.audio) {
					this.audio = webRTC._createAudioElement(false);
				}

				this.audio.srcObject = stream;
				if (!this.meter) {
					const peerId = this.id;
					this.meter = new SoundMeter(this.id, event.streams[0], (level) => webRTC._onSpeakerLevel(peerId, level));
				} else {
					this.meter.connectToSource(event.streams[0]);
				}
			} else if (event.track.kind === 'video') {
				this.video = stream;
				state.addVideoStream(this.video, this.id);
			}

			stream.onremovetrack = t => {
				if (event.track.kind == 'audio' && this.audio) {
					this.audio.remove();
					this.audio = null;
				}
				if (event.track.kind == 'video') {
					state.stopVideoTrack(this.video);
					this.video = null;
				}
			};
		};

		this.conn.onconnectionstatechange = event => {
			if ('failed' === this.conn.connectionState) {
				console.log(`${state.user.id}: removing peer (${this.conn.connectionState}): ${this.id}`);
				//this.remove();
			} else if ('disconnected' === this.conn.connectionState) {
				console.log(`${state.user.id}: removing peer (${this.conn.connectionState}): ${this.id}`);
				this.remove();
			}
		};

		this.conn.onicecandidateerror = event => {
			//@ts-ignore
			console.error('onicecandidateerror: ' + event.errorText + ' - ' + event.url);
		};

		this.conn.oniceconnectionstatechange = event => {
			console.log('icestate: ' + this.conn.iceConnectionState);
			//@ts-ignore
			let canRestart = this.conn.restartIce !== undefined && this.attempts < 2;
			let failed = this.conn.iceConnectionState === 'failed';
			if (failed && canRestart) {
				console.error('ice failed, restarting..');
				/* possibly reconfigure the connection in some way here */
				/* then request ICE restart */
				this.attempts++;
				//@ts-ignore
				this.conn.restartIce();
			} else if (failed) {
				console.error('ice failed');
				openToast('Sorry, your computer is not directly accessible to the internet and GatherAct cannot connect your audio/video', true);
			}
		};
	}

	async onnegotiationneeded() {
		try {
			let pc = this.conn;
			this.makingOffer = true;
			const offer = await pc.createOffer();
			if (pc.signalingState != 'stable') return;
			await pc.setLocalDescription(offer);

			transport
				.sendSignal(this.id, {
					type: 'webrtc_offer',
					payload: {
						peer_id: state.user.id,
						offer: pc.localDescription.toJSON()
					}
				})
				.catch(err => console.error('webrtc_offer signal failed', err));
		} catch (e) {
			console.error(`ONN ${e}`);
		} finally {
			this.makingOffer = false;
		}
	}

	async onOfferOrCandidate(description: RTCSessionDescriptionInit, candidate: RTCIceCandidateInit) {
		// Serialize signaling per peer so concurrent offers/answers don't
		// interleave setLocalDescription / setRemoteDescription calls.
		this.signalingChain = this.signalingChain
			.catch(() => {})
			.then(() => this._handleSignal(description, candidate));
		return this.signalingChain;
	}

	private async _handleSignal(
		description: RTCSessionDescriptionInit,
		candidate: RTCIceCandidateInit
	) {
		const pc = this.conn;
		try {
			if (description) {
				const offerCollision =
					description.type == 'offer' &&
					(this.makingOffer || pc.signalingState != 'stable');

				this.ignoreOffer = !polite && offerCollision;
				if (this.ignoreOffer) {
					return;
				}
				// An answer is only valid while we have a local offer outstanding.
				// If we've already rolled back or never sent the offer, dropping
				// it avoids "Called in wrong state: stable".
				if (description.type == 'answer' && pc.signalingState != 'have-local-offer') {
					return;
				}
				// Modern Chrome supports implicit rollback inside
				// setRemoteDescription when an offer arrives mid-negotiation,
				// so we no longer need a manual Promise.all rollback.
				await pc.setRemoteDescription(description);
				if (description.type == 'offer') {
					// Implicit createAnswer + setLocalDescription.
					await pc.setLocalDescription();
					transport
						.sendSignal(this.id, {
							type: 'webrtc_answer',
							payload: {
								peer_id: state.user.id,
								answer: pc.localDescription.toJSON()
							}
						})
						.catch(err => console.error('webrtc_answer signal failed', err));
				}
			} else if (candidate) {
				try {
					await pc.addIceCandidate(candidate);
				} catch (e) {
					if (!this.ignoreOffer) console.error(e);
				}
			}
		} catch (e) {
			console.error(e);
		}
	}

	remove() {
		this.conn.close();
		let found = peers.get(this.id);
		if (found === this) {
			peers.delete(this.id);
		}

		if (this.audio) this.audio.remove();
		if (this.video) {
			state.stopVideoTrack(this.video);
			this.video = null;
		}
	}
}

class WebRTC {
	/* mic/speaker options */
	audioInputDeviceOptions: ISelectItem[] = [];
	audioInputDeviceId: string = null;

	audioOutputDeviceOptions: ISelectItem[] = [];
	audioOutputDeviceId: string = null;

	videoInputDeviceOptions: ISelectItem[] = [];
	videoInputDeviceId: string = null;

	noiseSuppressionEnabled = true;
	echoCancellationEnabled = true;
	autoGainControlEnabled = true;
	private qualityInterval: ReturnType<typeof setInterval> | null = null;
	private speakerTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

	constructor() {}

	async setAudioOutputDevice(deviceId: string) {
		if (deviceId && deviceId !== this.audioOutputDeviceId) {
			try {
				this.audioOutputDeviceId = deviceId;
				let audioOutputs = document.querySelectorAll('audio');
				audioOutputs.forEach(element => {
					//@ts-ignore
					if (typeof element.sinkId !== 'undefined') {
						if (this.audioOutputDeviceId) {
							//@ts-ignore
							element.setSinkId(this.audioOutputDeviceId);
						}
					} else {
						console.warn('Browser does not support output device selection.');
					}
				});
			} catch (e) {
				this.handleError(e);
			}
		}
	}

	async setVideoInputDevice(deviceId: string) {
		if (deviceId && deviceId !== this.videoInputDeviceId) {
			// Set the input deviceId
			this.videoInputDeviceId = deviceId;
			this.enableCam(true);
		}
	}

	async setAudioInputDevice(deviceId: string) {
		if (deviceId && deviceId !== this.audioInputDeviceId) {
			try {
				if (deviceId == '0') {
					var constraints: MediaStreamConstraints = {
						audio: {
							noiseSuppression: this.noiseSuppressionEnabled,
							echoCancellation: this.echoCancellationEnabled,
							autoGainControl: this.autoGainControlEnabled
						},
						video: false
					};
				} else if (local_video_stream) {
					var constraints: MediaStreamConstraints = {
						audio: {
							deviceId: deviceId ? { exact: deviceId } : undefined,
							noiseSuppression: this.noiseSuppressionEnabled,
							echoCancellation: this.echoCancellationEnabled,
							autoGainControl: this.autoGainControlEnabled
						},
						video: { deviceId: this.videoInputDeviceId ? { exact: this.videoInputDeviceId } : undefined }
					};
				} else {
					var constraints: MediaStreamConstraints = {
						audio: {
							deviceId: deviceId ? { exact: deviceId } : undefined,
							noiseSuppression: this.noiseSuppressionEnabled,
							echoCancellation: this.echoCancellationEnabled,
							autoGainControl: this.autoGainControlEnabled
						},
						video: false
					};
				}

				if (local_media_stream) {
					this._stopSharingTracks(local_media_stream);
					local_media_stream = null;
				}

				local_media_stream = await navigator.mediaDevices.getUserMedia(constraints);

				// Set up level meter
				// if (!localMeter) {
				// 	localMeter = new SoundMeter('me', local_media_stream);
				// }

				if (!local_media) {
					local_media = this._createAudioElement(true);
				}
				local_media.srcObject = local_media_stream;
				this._shareTracks(local_media_stream);
			if (state.user?.id) {
				if (!localMeter) {
					localMeter = new SoundMeter('me', local_media_stream, (level) => webRTC._onSpeakerLevel(state.user.id, level));
				} else {
					localMeter.connectToSource(local_media_stream);
				}
			}
				// Set the input deviceId
				this.audioInputDeviceId = deviceId;
			} catch (e) {
				this.handleError(e);
			}
		}
	}

	_shareTracks(steam: MediaStream) {
		state.users.forEach(u => {
			if (u.me) return;
			let peer = peers.get(u.id);
			if (!peer) {
				peer = this._addPeerConnection(u.id);
			}

			let senders = peer.conn.getSenders();
			steam.getTracks().forEach(t => {
				if (!senders.some(s => s.track === t)) {
					peer.conn.addTrack(t, steam);
				}
			});
		});
	}

	_stopSharingTracks(stream: MediaStream) {
		let tracks = stream.getTracks();
		tracks.forEach(t => t.stop());
		for (let p of peers.values()) {
			let senders = p.conn.getSenders();
			senders.forEach(sender => {
				if (tracks.includes(sender.track)) {
					p.conn.removeTrack(sender);
				}
			});
		}
	}

	/**
	 * When we join a group, our signaling server will send out 'addPeer' events to each pair
	 * of users in the group (creating a fully-connected graph of users, ie if there are 6 people
	 * in the channel you will connect directly to the other 5, so there will be a total of 15
	 * connections in the network).
	 */
	_addPeerConnection(peer_id: string): Peer {
		if (peer_id in peers) {
			/* This could happen if the user joins multiple channels where the other peer is also in. */
			let error = `Already connected to peer ${peer_id}`;
			throw new Error(error);
		}

		let peer = new Peer(peer_id);
		peers.set(peer_id, peer);
		this.startQualityPolling();

		/* Add our local stream */
		if (local_media_stream) {
			local_media_stream.getTracks().forEach(t => peer.conn.addTrack(t, local_media_stream));
		}
		if (local_video_stream) {
			local_video_stream.getTracks().forEach(t => peer.conn.addTrack(t, local_video_stream));
		}

		return peer;
	}

	closeAllConnections() {
		this.stopQualityPolling();
		for (const t of this.speakerTimeouts.values()) clearTimeout(t);
		this.speakerTimeouts.clear();
		if (local_media_stream) {
			local_media_stream.getTracks().forEach(t => {
				t.stop();
			});
			local_media_stream = null;

			if (local_media) {
				local_media.remove();
				local_media = null;
			}
		}

		this.audioInputDeviceId = null;
		this.audioOutputDeviceId = null;

		for (let p of peers.values()) {
			p.remove();
		}
	}

	startConnection(peer_id: string) {
		try {
			this._addPeerConnection(peer_id);
		} catch (err) {
			console.error(err);
		}
	}

	async setAudioOptions() {
		// Set the audio input/output devices
		const devices = await navigator.mediaDevices.enumerateDevices();
		const inputOptions: ISelectItem[] = [];
		const outputOptions: ISelectItem[] = [];
		const videoOptions: ISelectItem[] = [];

		for (let device of devices) {
			let label = device.label;

			let list: ISelectItem[] = null;
			switch (device.kind) {
				case 'audioinput':
					list = inputOptions;
					if (!label) label = 'microphone';
					break;
				case 'audiooutput':
					list = outputOptions;
					if (!label) label = 'speaker';
					break;
				case 'videoinput':
					list = videoOptions;
					if (!label) label = 'camera';
					break;
			}

			if (!list) return;

			let found = list.some(i => i.name.includes(label));
			if (!found) {
				list.push({
					value: device.deviceId,
					name: label
				});
			}
		}
		this.audioInputDeviceOptions = inputOptions;
		this.audioOutputDeviceOptions = outputOptions;
		this.videoInputDeviceOptions = videoOptions;
	}

	/***********************/
	/** Local media stuff **/
	/***********************/
	async setup_local_media(): Promise<void> {
		if (local_media_stream != null || !navigator.mediaDevices) {
			return;
		}

		try {
			/* Ask user for permission to use the computers microphone and/or camera,
			 * attach it to an <audio> or <video> tag if they give us access. */
			const constraints: MediaStreamConstraints = {
				audio: { deviceId: this.audioInputDeviceId ? { exact: this.audioInputDeviceId } : undefined },
				video: false
			};
			await navigator.mediaDevices.getUserMedia(constraints);

			await this.setAudioOptions();

			// Set the audio input device
			if (this.audioInputDeviceOptions.length) {
				await this.setAudioInputDevice(this.audioInputDeviceOptions[0].value);
			}
			// Set the audio output device
			if (this.audioOutputDeviceOptions.length) {
				await this.setAudioOutputDevice(this.audioOutputDeviceOptions[0].value);
			} else {
				await this.setAudioOutputDevice('0');
			}
		} catch (e) {
			this.handleError(e);
		}
	}

	_createAudioElement(muted: boolean): HTMLAudioElement {
		let container = document.querySelector('#audioContainer');
		let local_media = document.createElement('audio');
		local_media.setAttribute('autoplay', 'autoplay');
		local_media.muted = muted;
		//@ts-ignore
		if (typeof local_media.sinkId !== 'undefined') {
			if (this.audioOutputDeviceId) {
				//@ts-ignore
				local_media.setSinkId(this.audioOutputDeviceId);
			}
		} else {
			console.warn('Browser does not support output device selection.');
		}
		container.append(local_media);
		local_media.removeAttribute('controls');
		return local_media;
	}

	enableMic(enabled: boolean) {
		if (!local_media_stream) {
			if (enabled) {
				webRTC.setup_local_media();
			}
			return;
		}
		local_media_stream.getAudioTracks()[0].enabled = enabled; // or false to mute it.
	}

	enablePeer(peer_id: string, enabled: boolean) {
		let peer = peers.get(peer_id);
		if (peer && peer.audio) {
			peer.audio.muted = !enabled;
		}
	}

	async screenShare() {
		if (local_video_stream) {
			let wasSharing = state.sharingScreen;
			this.stopVideoShare();
			if (wasSharing) return;
		}

		try {
			//@ts-ignore
			local_video_stream = await navigator.mediaDevices.getDisplayMedia();
			state.sharingScreen = true;
			// Screen share takes over the main view — gallery is not allowed.
			if (state.videoLayout === 'gallery') state.videoLayout = 'speaker';
			state.addVideoStream(local_video_stream, state.user.id);

			let videoTrack = local_video_stream.getVideoTracks()[0];
			videoTrack.onended = this.stopVideoShare.bind(this);
			this._shareTracks(local_video_stream);
		} catch (err) {
			if (err.code === 0) return;
			alert(err);
		}
	}

	async enableCam(changeSource = false) {
		if (local_video_stream) {
			let wasSharing = state.sharingCam;
			this.stopVideoShare();
			if (wasSharing && !changeSource) return;
		}

		try {
			if (!this.videoInputDeviceId) {
				await this.setAudioOptions(); // Get list of cameras etc.
				if (this.videoInputDeviceOptions.length) {
					this.videoInputDeviceId = this.videoInputDeviceOptions[0].value;
				} else {
					openToast('No Camera Found', true);
					return;
				}
			}
			const constraints: MediaStreamConstraints = {
				audio: false,
				video: { deviceId: this.videoInputDeviceId ? { exact: this.videoInputDeviceId } : undefined }
			};
			local_video_stream = await navigator.mediaDevices.getUserMedia(constraints);

			state.sharingCam = true;
			state.addVideoStream(local_video_stream, state.user.id);

			let videoTrack = local_video_stream.getVideoTracks()[0];
			videoTrack.onended = this.stopVideoShare.bind(this);
			this._shareTracks(local_video_stream);
		} catch (err) {
			alert(err);
		}
	}

	stopVideoShare() {
		let tracks = local_video_stream.getVideoTracks();
		if (tracks.length == 0) return;

		let videoTrack = tracks[0];
		videoTrack.stop();

		this._stopSharingTracks(local_video_stream);

		state.sharingScreen = false;
		state.sharingCam = false;
		state.stopVideoTrack(local_video_stream);
		local_video_stream = null;
	}

	onWebRtcMessage(peer_id: string, desc: RTCSessionDescriptionInit, candidate: RTCIceCandidateInit) {
		let peer = peers.get(peer_id);
		if (!peer) {
			peer = this._addPeerConnection(peer_id);
		}
		peer.onOfferOrCandidate(desc, candidate);
	}

	removePeer(peer_id: string) {
		let p = peers.get(peer_id);
		if (p) p.remove();
	}

	getStats() {
		for (let p of peers.values()) {
			p.conn.getStats().then(r => {
				r.forEach(s => console.log(s));
			});
		}
	}

	handleError(error: Error) {
		console.error('error: ', error.message, error.name);
	}

	_onSpeakerLevel(userId: string, level: number) {
		if (!userId) return;
		if (level > 0.01) {
			if (userId === state.activeSpeakerId) return;
			if (!this.speakerTimeouts.has(userId)) {
				const t = setTimeout(() => {
					this.speakerTimeouts.delete(userId);
					state.setActiveSpeaker(userId);
				}, 1500);
				this.speakerTimeouts.set(userId, t);
			}
		} else {
			const t = this.speakerTimeouts.get(userId);
			if (t) {
				clearTimeout(t);
				this.speakerTimeouts.delete(userId);
			}
		}
	}

	startQualityPolling() {
		if (this.qualityInterval) return;
		this.qualityInterval = setInterval(() => this._pollQuality(), 3000);
	}

	stopQualityPolling() {
		if (this.qualityInterval) {
			clearInterval(this.qualityInterval);
			this.qualityInterval = null;
		}
	}

	async _pollQuality() {
		for (const [peerId, peer] of peers) {
			try {
				const stats = await peer.conn.getStats();
				let rtt: number | null = null;
				stats.forEach((s: any) => {
					if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.currentRoundTripTime != null) {
						rtt = s.currentRoundTripTime;
					}
				});
				const quality: 'good' | 'fair' | 'poor' =
					rtt === null ? 'good' :
					rtt < 0.15 ? 'good' :
					rtt < 0.4 ? 'fair' : 'poor';
				state.setConnectionQuality(peerId, quality);
			} catch { /* ignore stats errors */ }
		}
	}

	async applyAudioProcessing() {
		if (this.audioInputDeviceId) {
			const saved = this.audioInputDeviceId;
			this.audioInputDeviceId = null;
			await this.setAudioInputDevice(saved);
		}
	}

}

export const webRTC = new WebRTC();

interface ISelectItem {
	value: string;
	name: string;
	//groupId: string;
}
